/**
 * Document Import Loading Bar
 *
 * Global loading bar that persists across navigation during document processing.
 * Appears below navigation header with animated progress indicator.
 * THEME-AWARE
 */

import React, { useMemo, useRef, useEffect } from 'react';
import { View, Text, StyleSheet, Animated } from 'react-native';
import { useDocumentImportContext } from '../../contexts/DocumentImportContext';
import { useTheme } from '../../contexts/ThemeContext';

export function DocumentImportLoadingBar() {
  const { theme } = useTheme();
  const { showLoadingBar, activeImport, processingProgress } = useDocumentImportContext();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);

  // Animate progress bar width
  const progressAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.timing(progressAnim, {
      toValue: processingProgress,
      duration: 300,
      useNativeDriver: false,
    }).start();
  }, [processingProgress, progressAnim]);

  if (!showLoadingBar || !activeImport) {
    return null;
  }

  const getStatusText = () => {
    switch (activeImport.status) {
      case 'pending':
        return 'Queued...';
      case 'processing':
        return `Processing... ${processingProgress}%`;
      case 'complete':
        return 'Complete!';
      default:
        return 'Processing...';
    }
  };

  const progressWidth = activeImport.status === 'complete' ? '100%' : `${processingProgress}%`;

  return (
    <View style={[styles.container, { backgroundColor: theme.colors.backgroundElevated }]}>
      <View style={styles.content}>
        <Text style={[styles.statusText, { color: theme.colors.text }]}>{getStatusText()}</Text>
        {activeImport.fileName && (
          <Text style={[styles.fileName, { color: theme.colors.textSecondary }]}>
            {activeImport.fileName}
          </Text>
        )}
      </View>

      {/* Progress bar background */}
      <View style={[styles.progressBarBg, { backgroundColor: theme.colors.border }]}>
        {/* Animated progress bar */}
        <Animated.View
          style={[
            styles.progressBarFill,
            {
              backgroundColor: theme.colors.primary,
              width: progressWidth,
            },
          ]}
        />
      </View>
    </View>
  );
}

function createThemedStyles(theme: any) {
  return StyleSheet.create({
    container: {
      borderBottomWidth: theme.id === 'bauhaus' ? 2 : 1,
      borderBottomColor: theme.colors.border,
      paddingHorizontal: theme.spacing.md,
      paddingTop: theme.spacing.sm,
      paddingBottom: theme.spacing.xs,
      zIndex: 10,
    },
    content: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: theme.spacing.xs,
    },
    statusText: {
      fontSize: theme.typography.fontSizeSm,
      fontWeight: theme.typography.fontWeightMedium,
      textTransform: theme.id === 'bauhaus' ? 'uppercase' : 'none',
    },
    fileName: {
      fontSize: theme.typography.fontSizeXs,
    },
    progressBarBg: {
      height: 4,
      borderRadius: 2,
      overflow: 'hidden',
    },
    progressBarFill: {
      height: '100%',
      borderRadius: 2,
    },
  });
}
